import type { Class, Project } from '../../data/types';
import type { ExportModelInput, ModelArtifacts } from '../../ml/trainer';
import type { PublishConfig } from '../types';

/**
 * Tiny structural interface the bundle pipeline depends on.
 *
 * Both `ImageTrainer` and `AudioTrainer` satisfy this — we only need
 * `extractArtifacts` to pull the trained model bytes out of the
 * trainer's IndexedDB store. Decoupling the bundle from the wider
 * trainer interfaces lets pose + audio (which use different runtime
 * surfaces) ship through the same publish path.
 */
export interface BundleArtifactsExtractor {
  extractArtifacts?(input: ExportModelInput): Promise<ModelArtifacts>;
}

/**
 * Inputs for the publish-bundle generator.
 *
 * The generator turns the kid's project + composer config into a
 * self-contained zip the kid can host anywhere. It needs the trained
 * model artefacts (via the trainer's IndexedDB-backed extractor),
 * the class list (label order matters: the trained model's output
 * indices line up with the class-list order at training time), and
 * the composer's configuration.
 *
 * Pure-ish: the only side effect is going through the trainer to read
 * IndexedDB. Tests can substitute a fake trainer.
 */
export interface BuildPublishBundleInput {
  project: Pick<Project, 'id' | 'name' | 'kind'>;
  classes: ReadonlyArray<Pick<Class, 'id' | 'name'>>;
  config: PublishConfig;
  trainer: BundleArtifactsExtractor;
}

/** Map of relative paths inside the zip → file contents. Strings are
 *  UTF-8 encoded text (HTML / JS / JSON), Uint8Arrays are binary
 *  (model weights). */
export interface PublishFileMap {
  files: Record<string, string | Uint8Array>;
}

export interface BuildPublishBundleResult {
  /** The packed bundle, ready for the browser to download. */
  blob: Blob;
  /** Same bytes that back the blob — exposed so tests can verify the
   *  zip contents without going through `Blob.arrayBuffer()` (which
   *  jsdom corrupts on binary payloads). Production callers prefer
   *  `blob`. */
  bytes: Uint8Array;
  /** Suggested download filename, e.g. `bryta-show-off-demo-app.zip`. */
  filename: string;
}

/**
 * The reasons a project might not be downloadable yet. Surfaces to the
 * UI as "Download isn't available because…" copy.
 */
export type PublishBundleBlocker =
  | 'no-trained-model' // trainer can't produce model artefacts
  | 'unsupported-kind' // bundle support not wired for this project kind
  | 'unsupported-template'; // bundle support not wired for this template

export class PublishBundleError extends Error {
  constructor(
    public readonly blocker: PublishBundleBlocker,
    message: string,
  ) {
    super(message);
    this.name = 'PublishBundleError';
  }
}
